/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Devis\TvaStatusAlert.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * ALERTE CHANGEMENT DE RÉGIME TVA
 * 
 * Composant qui détecte et alerte l'utilisateur lorsqu'un devis a été créé
 * avec un régime TVA différent de celui actuellement configuré pour l'entreprise.
 * 
 * FONCTIONNALITÉS:
 * - Détection automatique de la différence de régime TVA
 * - Bandeau d'information explicite
 * - Option 1: Conserver le devis tel quel (recommandé - respect du contexte fiscal d'origine)
 * - Option 2: Dupliquer le devis et recalculer avec le nouveau régime TVA
 * 
 * CONTEXTE LÉGAL:
 * Un devis est une "photo" du contexte fiscal au moment de sa création (Art. 293 B du CGI).
 * Il est recommandé de le conserver tel quel pour respecter l'engagement contractuel.
 */

import React, { useState, useEffect } from 'react';

interface TvaStatusAlertProps {
  devisSoumisTva: boolean;
  devisTauxTva: number;
  entrepriseSoumisTva: boolean;
  entrepriseTauxTva: number;
  devisReference: string;
  onConserver: () => void;
  onDupliquer: () => void;
}

const TvaStatusAlert: React.FC<TvaStatusAlertProps> = ({
  devisSoumisTva,
  devisTauxTva,
  entrepriseSoumisTva,
  entrepriseTauxTva,
  devisReference,
  onConserver,
  onDupliquer
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // ✅ CORRECTION : Attendre 500ms avant d'afficher l'alerte
  // Cela évite les faux positifs lors du chargement initial ou après duplication
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Vérifier s'il y a une différence
  const hasDifference = devisSoumisTva !== entrepriseSoumisTva || devisTauxTva !== entrepriseTauxTva;

  // 🔍 LOGS DEBUG

  // Si pas de différence ou si l'alerte a été fermée ou pas encore prête, ne rien afficher
  if (!hasDifference || isDismissed || !isReady) {

    return null;
  }

  const handleConserver = () => {
    setIsProcessing(true);
    onConserver();
    setIsDismissed(true);
  };

  const handleDupliquer = async () => {
    setIsProcessing(true);
    try {
      await onDupliquer();
    } finally {
      setIsProcessing(false);
    }
  };

  // Déterminer le message selon le type de changement
  const getMessage = () => {
    if (devisSoumisTva && !entrepriseSoumisTva) {
      return {
        titre: "Changement de régime TVA détecté",
        description: `Ce devis a été créé lorsque l'entreprise était soumise à la TVA (${devisTauxTva}%). Votre entreprise est maintenant en franchise de TVA (article 293 B du CGI).`,
        devisOrigine: `Soumis à TVA ${devisTauxTva}%`,
        entrepriseActuelle: "Franchise en base de TVA (non soumis)"
      };
    } else if (!devisSoumisTva && entrepriseSoumisTva) {
      return {
        titre: "Changement de régime TVA détecté",
        description: `Ce devis a été créé lorsque l'entreprise était en franchise de TVA. Votre entreprise est maintenant soumise à la TVA (${entrepriseTauxTva}%).`,
        devisOrigine: "Franchise en base de TVA (non soumis)",
        entrepriseActuelle: `Soumis à TVA ${entrepriseTauxTva}%`
      };
    } else {
      return {
        titre: "Taux de TVA différent détecté",
        description: `Ce devis a été créé avec un taux de TVA de ${devisTauxTva}%. Le taux actuel de votre entreprise est de ${entrepriseTauxTva}%.`,
        devisOrigine: `TVA ${devisTauxTva}%`,
        entrepriseActuelle: `TVA ${entrepriseTauxTva}%`
      };
    }
  };

  const message = getMessage();

  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-6 rounded-r-lg shadow-md">
      {/* Icône et titre */}
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-8 w-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <div className="ml-4 flex-1">
          {/* Titre */}
          <h3 className="text-lg font-semibold text-amber-800 mb-2">
            {message.titre}
          </h3>

          {/* Description */}
          <p className="text-sm text-amber-700 mb-4">
            {message.description}
          </p>

          {/* Détails du régime TVA */}
          <div className="bg-white rounded-lg p-4 mb-4 border border-amber-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Devis d'origine ({devisReference})
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {message.devisOrigine}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Statut actuel de l'entreprise
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {message.entrepriseActuelle}
                </div>
              </div>
            </div>
          </div>

          {/* Note légale */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-blue-700">
                <strong>Note légale :</strong> Un devis constitue un engagement contractuel au prix indiqué. 
                Il est recommandé de le conserver tel quel pour respecter le contexte fiscal dans lequel il a été établi.
              </p>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Option 1 : Conserver (recommandé) */}
            <button
              type="button"
              onClick={handleConserver}
              disabled={isProcessing}
              className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Conserver tel quel <span className="ml-2 text-xs">(recommandé)</span>
            </button>

            {/* Option 2 : Dupliquer et recalculer */}
            <button
              type="button"
              onClick={handleDupliquer}
              disabled={isProcessing}
              className="flex-1 inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Duplication en cours...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Dupliquer & recalculer
                </>
              )}
            </button>
          </div>

          {/* Explication des options */}
          <div className="mt-4 text-xs text-gray-600 space-y-1">
            <p>
              <strong>• Conserver tel quel :</strong> Le devis reste inchangé avec son régime TVA d'origine (recommandé pour respecter l'engagement contractuel).
            </p>
            <p>
              <strong>• Dupliquer & recalculer :</strong> Crée un nouveau devis avec les mêmes articles mais recalculés selon le régime TVA actuel de l'entreprise.
            </p>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          className="flex-shrink-0 ml-4 text-amber-400 hover:text-amber-600 transition-colors duration-200"
          title="Fermer cette alerte"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TvaStatusAlert;
